import React, { useState, useEffect } from "react";
import axios from "axios";
import { Helmet } from "react-helmet-async";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useUpload } from "../context/UploadContext";
import CONFIG from "../config";
import BRANDING from "../branding";

const compressImage = async (file, maxWidth = 1200, quality = 0.7) => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;

        if (width > maxWidth) {
          height = (maxWidth / width) * height;
          width = maxWidth;
        }

        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob((blob) => {
          const compressedFile = new File([blob], file.name.split('.')[0] + '.webp', {
            type: 'image/webp',
            lastModified: Date.now(),
          });
          resolve(compressedFile);
        }, 'image/webp', quality);
      };
    };
  });
};

const CreateProduct = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { addTask } = useUpload();
  const isArabic = i18n.language?.startsWith('ar');

  const [formData, setFormData] = useState({
    name: "",
    name_ar: "",
    description: "",
    description_ar: "",
    title: "",
    content: "",
    price: "",
    price_egp: "",
    price_sdg: "",
    category: [],
    newCategory: "",
    newCategoryAr: "",
    color: "",
    color_ar: "",
    material: "",
    material_ar: "",
    dimensions: "",
    dimensions_ar: "",
    image: null,
    thumbnails: [],
    video: null,
  });


  const [categories, setCategories] = useState([]);
  const [existingFilters, setExistingFilters] = useState({ color: [], material: [], dimensions: [] });
  const [previewMain, setPreviewMain] = useState(null);
  const [previewThumbs, setPreviewThumbs] = useState([]);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [catRes, filterRes] = await Promise.all([
          axios.get(`${CONFIG.API_URL}/api/categories`),
          axios.get(`${CONFIG.API_URL}/api/products/filters`)
        ]);
        setCategories(catRes.data || []);
        setExistingFilters({
          color: filterRes.data?.color || [],
          material: filterRes.data?.material || [],
          dimensions: filterRes.data?.dimensions || []
        });

        // Check for retry data
        if (location.state?.retryData?.formData) {
          const retry = location.state.retryData.formData;
          setFormData(prev => ({
            ...prev,
            ...retry,
            image: null, // Files cannot be recovered from JSON
            thumbnails: [],
            video: null
          }));
          setMessage("💡 Form pre-filled from task history. Please re-select your media files.");
        }
      } catch (err) {
        console.log("Error loading metadata", err);
      }
    };
    loadData();
  }, [location.state]);

  useEffect(() => {
    setPreviewThumbs(formData.thumbnails.map(f => URL.createObjectURL(f)));
  }, [formData.thumbnails]);

  const handleChange = (e) => {
    const { name, value, files, options, type } = e.target;
    if (name === "image") {
      setFormData(prev => ({ ...prev, image: files[0] }));
      setPreviewMain(URL.createObjectURL(files[0]));
    } else if (name === "video") {
      setFormData(prev => ({ ...prev, video: files[0] }));
    } else if (name === "thumbnails") {
      const newFiles = Array.from(files);
      setFormData(prev => {
        const combined = [...prev.thumbnails, ...newFiles].slice(0, 4);
        return { ...prev, thumbnails: combined };
      });
    } else if (name === "category" && type === "select-multiple") {
      const selected = Array.from(options).filter(o => o.selected).map(o => o.value);
      setFormData(prev => ({ ...prev, category: selected }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const removeThumbnailAt = (index) => {
    setFormData(prev => {
      const newThumbs = [...prev.thumbnails];
      newThumbs.splice(index, 1);
      return { ...prev, thumbnails: newThumbs };
    });
  };

  const handleAddCategory = async () => {
    if (!formData.newCategory.trim()) return;
    try {
      setLoading(true);
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const res = await axios.post(`${CONFIG.API_URL}/api/categories`,
        { name: formData.newCategory, name_ar: formData.newCategoryAr },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCategories(prev => {
        const exists = prev.find(c => c.id === res.data.id);
        if (exists) return prev;
        return [...prev, res.data].sort((a, b) => a.name.localeCompare(b.name));
      });
      setFormData(prev => ({
        ...prev,
        category: [...prev.category, res.data.id.toString()],
        newCategory: "",
        newCategoryAr: ""
      }));
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
      setMessage(`❌ Failed to add category: ${err.response?.data?.message || err.message}`);
    }
  };

  const handleGenerateVideo = async () => {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    if (!token) {
      setMessage("\u274c You must be logged in to generate a video.");
      return;
    }

    try {
      // Extract title and content from formData
      const title = formData.title || '';
      const content = formData.content || '';

      // Send to correct API endpoint
      await axios.post("http://localhost:5000/api/stories", {
        title,
        content
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMessage("\u2713 Story created and video generation started!");
    } catch (error) {
      console.error("Video generation error:", error);
      setMessage("\u274c Failed to generate video.");
    }
  };


  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (loading) return;

    try {
      setLoading(true);
      setMessage("⏳ Task added to background. You can continue browsing!");

      addTask('CREATE', { formData, categories }, { compressImage });

      setLoading(false);
      // Reset form or navigate? 
      // User said "even to create another products", so let's reset form for another creation
      // but also provide a link to go to list
      setFormData({
        name: "", name_ar: "", description: "", description_ar: "", title: "", content: "",
        price: "", price_egp: "", price_sdg: "",
        category: [], newCategory: "", newCategoryAr: "",
        color: "", color_ar: "", material: "", material_ar: "", dimensions: "", dimensions_ar: "",
        image: null, thumbnails: [], video: null
      });
      setPreviewMain(null);
      setPreviewThumbs([]);

      setTimeout(() => setMessage(""), 5000);
    } catch (err) {
      console.error(err);
      setLoading(false);
      const status = err.response?.status;
      if (status === 401) {
        setMessage("❌ Session expired. Please log in again.");
      } else {
        setMessage(`❌ ${err.response?.data?.message || err.message || "Failed to start background task."}`);
      }
    }
  };

  const GroupTitle = ({ title, icon }) => (
    <div className="flex items-center gap-3 mb-6 pb-2 border-b border-gray-100">
      <div className="w-10 h-10 rounded-xl bg-brand-primary/10 flex items-center justify-center text-brand-primary">
        <i className={`fas ${icon}`}></i>
      </div>
      <h3 className="text-lg font-black text-gray-900 uppercase tracking-tight">{title}</h3>
    </div>
  );

  const MultiSelectField = ({ label, name, options, value, arValue, placeholder }) => {
    const [typed, setTyped] = useState("");
    const [typedAr, setTypedAr] = useState("");

    const addValue = () => {
      if (!typed.trim()) return;
      const newVal = value ? `${value}, ${typed.trim()}` : typed.trim();
      const newValAr = arValue ? `${arValue}، ${typedAr.trim() || typed.trim()}` : (typedAr.trim() || typed.trim());
      setFormData(prev => ({ ...prev, [name]: newVal, [`${name}_ar`]: newValAr }));
      setTyped("");
      setTypedAr("");
    };

    const handleSelectExisting = (opt) => {
      const isSelected = value?.split(", ").includes(opt.name);
      if (isSelected) {
        const newVal = value.split(", ").filter(v => v !== opt.name).join(", ");
        const newValAr = arValue.split("، ").filter(v => v !== opt.name_ar).join("، ");
        setFormData(prev => ({ ...prev, [name]: newVal, [`${name}_ar`]: newValAr }));
      } else {
        const newVal = value ? `${value}, ${opt.name}` : opt.name;
        const newValAr = arValue ? `${arValue}، ${opt.name_ar || opt.name}` : (opt.name_ar || opt.name);
        setFormData(prev => ({ ...prev, [name]: newVal, [`${name}_ar`]: newValAr }));
      }
    };

    return (
      <div className="space-y-4 p-6 bg-gray-50/50 rounded-[30px] border border-gray-100">
        <label className="block text-xs font-black text-gray-400 uppercase tracking-widest px-1">{label}</label>

        <div className="flex flex-wrap gap-2 min-h-[40px]">
          {value?.split(", ").filter(Boolean).map((v, i) => (
            <span key={i} className="bg-white text-gray-900 px-4 py-2 rounded-xl text-xs font-bold shadow-sm border border-gray-100 flex items-center gap-2 group transition-all hover:border-red-200">
              {v}
              <button type="button" onClick={() => {
                const parts = value.split(", ");
                const partsAr = arValue.split("، ");
                parts.splice(i, 1);
                partsAr.splice(i, 1);
                setFormData(prev => ({ ...prev, [name]: parts.join(", "), [`${name}_ar`]: partsAr.join("، ") }));
              }} className="text-gray-400 group-hover:text-red-500">×</button>
            </span>
          ))}
          {!value && <p className="text-xs text-gray-400 italic py-2">{isArabic ? "لا توجد سمات محددة" : "No attributes selected"}</p>}
        </div>

        <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto p-4 bg-white rounded-2xl border border-gray-100 shadow-inner">
          {options.map((opt, i) => {
            const isSelected = value?.split(", ").includes(opt.name);
            return (
              <button
                key={i}
                type="button"
                onClick={() => handleSelectExisting(opt)}
                className={`text-xs px-3 py-2 rounded-xl transition-all font-bold ${isSelected ? "bg-brand-primary text-white shadow-lg shadow-brand-primary/20" : "bg-gray-50 text-gray-500 hover:bg-gray-100"}`}
              >
                {isArabic ? (opt.name_ar || opt.name) : opt.name}
              </button>
            );
          })}
        </div>

        <div className="flex flex-col sm:flex-row gap-2 pt-2">
          <input
            type="text"
            placeholder={`${placeholder}`}
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            className="flex-1 text-sm p-4 border border-gray-100 rounded-2xl bg-white focus:ring-4 focus:ring-brand-primary/10 transition-all outline-none font-medium"
          />
          <input
            type="text"
            placeholder={`${placeholder}`}
            value={typedAr}
            onChange={(e) => setTypedAr(e.target.value)}
            className="flex-1 text-sm p-4 border border-gray-100 rounded-2xl bg-white focus:ring-4 focus:ring-brand-primary/10 transition-all outline-none font-medium"
            dir="rtl"
          />
          <button
            type="button"
            onClick={addValue}
            className="bg-gray-900 text-white px-6 py-4 rounded-2xl text-xs font-black uppercase tracking-widest hover:bg-brand-primary transition-all active:scale-95"
          >
            {t('admin.common.add')}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#fcfcfc] py-12 px-4 sm:px-6 lg:px-8">
      <Helmet>
        <html lang={isArabic ? "ar" : "en"} dir={isArabic ? "rtl" : "ltr"} />
        <title>{t('admin.products_page.create')} | {isArabic ? BRANDING.appName.ar : BRANDING.appName.en}</title>
      </Helmet>

      <div className="max-w-4xl mx-auto">
        <header className="flex items-center justify-between mb-10">
          <div>
            <h1 className="text-3xl font-black text-gray-900 tracking-tight">{t('admin.products_page.create')}</h1>
            <p className="text-gray-500 font-medium mt-1">{isArabic && t('admin.products_page.description')}</p>
          </div>
          <button
            onClick={() => navigate("/admin/product")}
            className="flex items-center gap-2 text-gray-500 hover:text-brand-primary transition-all text-sm font-black uppercase tracking-widest group"
          >
            <span className={`transition-transform group-hover:-translate-x-1 ${isArabic ? "rotate-180 group-hover:translate-x-1" : ""}`}>←</span>
            {t('admin.common.back')}
          </button>
        </header>

        <form onSubmit={handleSubmit} className="space-y-8 bg-white p-8 sm:p-12 rounded-[40px] shadow-[0_20px_50px_rgba(0,0,0,0.03)] border border-gray-50">

          {/* Section 1: Basic Info */}
          <section>
            <GroupTitle title={t('admin.products_page.form.info_section')} icon="fa-info-circle" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-xs font-black text-gray-400 uppercase tracking-widest px-1">{t('admin.products_page.form.name_en')}</label>
                <input type="text" name="name" placeholder="E.g. Royal Velvet Sofa" value={formData.name} onChange={handleChange} className="w-full p-4 rounded-2xl border border-gray-100 bg-gray-50/50 focus:bg-white focus:ring-4 focus:ring-brand-primary/10 transition-all outline-none font-medium" required />
              </div>
              <div className="space-y-2 text-right">
                <label className="text-xs font-black text-gray-400 uppercase tracking-widest px-1">{t('admin.products_page.form.name_ar')}</label>
                <input type="text" name="name_ar" placeholder="مثال: كنبة ملكية مخمل" value={formData.name_ar} onChange={handleChange} className="w-full p-4 rounded-2xl border border-gray-100 bg-gray-50/50 focus:bg-white focus:ring-4 focus:ring-brand-primary/10 transition-all outline-none font-medium text-right" dir="rtl" />
              </div>
              <div className="md:col-span-2 space-y-2">
                <label className="text-xs font-black text-gray-400 uppercase tracking-widest px-1">{t('admin.products_page.form.desc_en')}</label>
                <textarea name="description" placeholder="A detailed story about this piece..." value={formData.description} onChange={handleChange} className="w-full p-4 rounded-2xl border border-gray-100 bg-gray-50/50 focus:bg-white focus:ring-4 focus:ring-brand-primary/10 transition-all outline-none font-medium min-h-[120px]" required />
              </div>
              <div className="md:col-span-2 space-y-2 text-right">
                <label className="text-xs font-black text-gray-400 uppercase tracking-widest px-1">{t('admin.products_page.form.desc_ar')}</label>
                <textarea name="description_ar" placeholder="قصة مفصلة عن هذه القطعة..." value={formData.description_ar} onChange={handleChange} className="w-full p-4 rounded-2xl border border-gray-100 bg-gray-50/50 focus:bg-white focus:ring-4 focus:ring-brand-primary/10 transition-all outline-none font-medium min-h-[120px] text-right" dir="rtl" />
              </div>
            </div>
          </section>
          <section>
            <div className="max-w-md mx-auto mt-16 bg-white shadow-lg p-8 rounded-2xl">
              <h2 className="text-2xl font-semibold mb-4 text-center">Create a Story</h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  placeholder="Story Title"
                  onChange={handleChange}
                  className="w-full p-2 border rounded"
                  required
                />
                <textarea
                  name="content"
                  value={formData.content}
                  placeholder="Write your story..."
                  rows="5"
                  onChange={handleChange}
                  className="w-full p-2 border rounded"
                  required
                />
                {/* <button
                  type="submit"
                  className="w-full bg-blue-600 text-white py-2 rounded hover:bg-blue-700"
                >
                  Submit
                </button> */}
                <button
                  type="button"
                  onClick={handleGenerateVideo}
                  className="w-full bg-green-600 text-white py-2 rounded hover:bg-green-700"
                >
                  Generate Story Video
                </button>
              </form>
              {message && <p className="text-center mt-4 text-gray-600">{message}</p>}
            </div>
          </section>

          {/* Section 2: Pricing */}
          <section>
            <GroupTitle title={t('admin.products_page.form.pricing_section')} icon="fa-tag" />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { name: 'price', label: 'USD', icon: 'fa-dollar-sign' },
                { name: 'price_egp', label: 'EGP', icon: 'fa-pound-sign' },
                { name: 'price_sdg', label: 'SDG', icon: 'fa-coins' }
              ].map(p => (
                <div key={p.name} className="space-y-2">
                  <label className="text-xs font-black text-gray-400 uppercase tracking-widest px-1">{p.label}</label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"><i className={`fas ${p.icon}`}></i></span>
                    <input type="number" name={p.name} value={formData[p.name]} onChange={handleChange} className="w-full pl-10 pr-4 py-4 rounded-2xl border border-gray-100 bg-gray-50/50 focus:bg-white focus:ring-4 focus:ring-brand-primary/10 transition-all outline-none font-black" />
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Section 3: Categories */}
          <section>
            <GroupTitle title={t('admin.products_page.category')} icon="fa-layer-group" />
            <div className="space-y-6">
              <div className="space-y-2">
                <label className="text-xs font-black text-gray-400 uppercase tracking-widest px-1">{t('admin.products_page.form.categories_select')}</label>
                <select name="category" value={formData.category} onChange={handleChange} multiple className="w-full p-4 rounded-2xl border border-gray-100 bg-gray-50/50 focus:bg-white focus:ring-4 focus:ring-brand-primary/10 transition-all outline-none font-medium h-48 shadow-inner overflow-y-auto">
                  {(categories || []).map((c) => (
                    <option key={c.id} value={c.id} className="p-3 rounded-xl m-1 hover:bg-brand-primary hover:text-white transition-colors cursor-pointer font-bold">
                      {isArabic ? (c.name_ar || c.name) : c.name}
                    </option>
                  ))}
                </select>
                <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest px-1 mt-2">{isArabic && t('admin.products_page.form.select_multiple')}</p>
              </div>

              <div className="p-6 bg-gray-50/50 rounded-[30px] border border-gray-100 space-y-4">
                <label className="block text-xs font-black text-gray-400 uppercase tracking-widest px-1">{isArabic && t('admin.products_page.form.or_create_new_category')}</label>
                <div className="flex flex-col sm:flex-row gap-2">
                  <input type="text" name="newCategory" placeholder={t('admin.products_page.form.new_category_en')} value={formData.newCategory} onChange={handleChange} className="flex-1 p-4 rounded-2xl border border-gray-100 bg-white focus:ring-4 focus:ring-brand-primary/10 transition-all outline-none font-medium text-sm" />
                  <input type="text" name="newCategoryAr" placeholder={t('admin.products_page.form.new_category_ar')} value={formData.newCategoryAr || ""} onChange={handleChange} className="flex-1 p-4 rounded-2xl border border-gray-100 bg-white focus:ring-4 focus:ring-brand-primary/10 transition-all outline-none font-medium text-sm text-right" dir="rtl" />
                  <button type="button" onClick={handleAddCategory} disabled={!formData.newCategory.trim() || loading} className="bg-gray-900 text-white px-6 py-4 rounded-2xl text-xs font-black uppercase tracking-widest hover:bg-brand-primary transition-all active:scale-95 disabled:bg-gray-300 disabled:cursor-not-allowed">
                    {t('admin.common.add')}
                  </button>
                </div>
              </div>
            </div>
          </section>

          {/* Section 4: Media */}
          <section>
            <GroupTitle title={t('admin.products_page.form.media_section')} icon="fa-images" />
            <div className="space-y-8">
              {/* Main Image */}
              <div className="space-y-4">
                <label className="text-xs font-black text-gray-400 uppercase tracking-widest px-1">{t('admin.products_page.form.main_image')}</label>
                <div className="flex flex-col sm:flex-row gap-6 items-center">
                  <div
                    className="w-40 h-40 rounded-3xl border-2 border-dashed border-gray-200 bg-gray-50 flex items-center justify-center overflow-hidden cursor-pointer hover:border-brand-primary hover:bg-brand-primary/5 transition-all group"
                    onClick={() => document.getElementById('main-image-input').click()}
                  >
                    {previewMain ? (
                      <img src={previewMain} alt="preview" className="w-full h-full object-cover" />
                    ) : (
                      <div className="text-center p-4">
                        <i className="fas fa-camera text-2xl text-gray-300 group-hover:text-brand-primary transition-colors"></i>
                      </div>
                    )}
                    <input id="main-image-input" type="file" name="image" accept="image/*" onChange={handleChange} className="hidden" aria-label="Main product image selection" />
                  </div>
                  <div className="flex-1 text-center sm:text-left">
                    <p className="text-sm font-bold text-gray-700">{previewMain && isArabic ? t('admin.products_page.form.main_image_set') : t('admin.products_page.form.no_file_choosen')}</p>
                    <p className="text-xs text-gray-400 mt-1">{isArabic && t('admin.products_page.form.first_image_customer_see')}</p>
                  </div>
                </div>
              </div>

              {/* Extra Images */}
              <div className="space-y-4">
                <label className="text-xs font-black text-gray-400 uppercase tracking-widest px-1">{t('admin.products_page.form.extra_images')}</label>
                <div
                  className="p-8 border-2 border-dashed border-gray-200 rounded-[30px] bg-gray-50/50 text-center cursor-pointer hover:border-brand-primary hover:bg-brand-primary/5 transition-all group"
                  onClick={() => document.getElementById('thumbnail-input').click()}
                >
                  <input id="thumbnail-input" type="file" name="thumbnails" multiple accept="image/*" onChange={handleChange} className="hidden" aria-label="Extra product images selection" />
                  <div className="flex flex-col items-center">
                    <div className="w-12 h-12 bg-white rounded-2xl shadow-sm flex items-center justify-center text-gray-400 group-hover:bg-brand-primary group-hover:text-white transition-all mb-4">
                      <i className="fas fa-images text-xl"></i>
                    </div>
                    <p className="font-black text-gray-900 uppercase tracking-widest text-xs mb-1">{t('admin.products_page.form.click_to_browse')}</p>
                    <p className="text-xs text-gray-400 font-medium">{t('admin.products_page.form.max_images_hint')}</p>
                  </div>
                </div>
                {previewThumbs.length > 0 && (
                  <div className="flex gap-4 flex-wrap mt-4">
                    {previewThumbs.map((s, i) => (
                      <div key={i} className="relative group/item w-24 h-24">
                        <img src={s} alt={`thumb-${i}`} className="w-full h-full object-cover rounded-2xl shadow-sm border border-gray-100" />
                        <button type="button" onClick={() => removeThumbnailAt(i)} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-8 h-8 flex items-center justify-center shadow-lg transform scale-0 group-hover/item:scale-100 transition-all hover:bg-red-600">
                          <i className="fas fa-times text-xs"></i>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Video */}
              <div className="space-y-4">
                <label className="text-xs font-black text-gray-400 uppercase tracking-widest px-1">{t('admin.products_page.form.video_optional')}</label>
                <div className="relative">
                  <input type="file" name="video" accept="video/*" onChange={handleChange} className="w-full p-4 border border-gray-100 rounded-2xl bg-gray-50/50 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-xs file:font-black file:uppercase file:tracking-widest file:bg-gray-900 file:text-white hover:file:bg-brand-primary transition-all font-medium" />
                </div>
              </div>
            </div>
          </section>

          {/* Section 5: Attributes */}
          <section>
            <GroupTitle title={t('admin.products_page.form.attributes_section')} icon="fa-sliders-h" />
            <div className="space-y-6">
              <MultiSelectField label={t('admin.form.color')} name="color" options={existingFilters.color} value={formData.color} arValue={formData.color_ar} placeholder={isArabic ? t('admin.products_page.form.new_color_ar') : t('admin.products_page.form.new_color_en')} />
              <MultiSelectField label={t('admin.form.material')} name="material" options={existingFilters.material} value={formData.material} arValue={formData.material_ar} placeholder={isArabic ? t('admin.products_page.form.new_material_ar') : t('admin.products_page.form.new_material_en')} />
              <MultiSelectField label={t('admin.form.dimensions')} name="dimensions" options={existingFilters.dimensions} value={formData.dimensions} arValue={formData.dimensions_ar} placeholder={isArabic ? t('admin.products_page.form.new_dimensions_ar') : t('admin.products_page.form.new_dimensions_en')} />
            </div>
          </section>

          {/* Progress / Messaging Area */}
          {message && (
            <div className={`p-4 rounded-2xl text-center text-sm font-bold ${message.includes('✅') ? 'bg-green-50 text-green-700 border border-green-100' : 'bg-red-50 text-red-700 border border-red-100'}`}>
              {message}
            </div>
          )}

          {/* Submit */}
          <div className="pt-6">
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gray-900 text-white py-6 rounded-[30px] font-black uppercase tracking-[0.2em] text-sm hover:bg-brand-primary hover:shadow-2xl hover:shadow-brand-primary/30 active:scale-[0.98] transition-all disabled:bg-gray-400 disabled:cursor-not-allowed shadow-xl shadow-gray-200"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="fas fa-circle-notch fa-spin"></i> {t('admin.common.loading')}
                </span>
              ) : t('admin.products_page.create')}
            </button>
          </div>

        </form>
      </div>
    </div>
  );
};

export default CreateProduct;
